import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PageContainer,
  Button,
  Badge,
  DataTable,
  Modal,
  FormField,
  Input,
  Select,
  TextArea,
  Skeleton,
  Tooltip,
  EmptyState,
  ErrorBoundary,
  FileUpload,
  Alert,
  useToast,
  ConfirmDialog,
  Toggle,
} from '@bbpos/ui';
import type { ColumnDef } from '@bbpos/ui';
import { categoryService } from '@/services/category.service';
import { useAuthStore } from '@/stores/use-auth-store';
import { useKeyboardShortcut } from '@/hooks/use-keyboard-shortcut';
import { ROLE_LEVELS } from '@/constants/product';
import { GLASS, GLASS_FILTER, cn, toneClasses } from '@/constants/design-tokens';
import { buildCategoryTree, validateCategoryName, stripHtml, CATEGORY_DESC_MAX, CATEGORY_IMAGE_ACCEPT, CATEGORY_IMAGE_MAX_SIZE } from '@/types/category';
import type {
  Category,
  CategoryWithChildren,
  CreateCategoryPayload,
  UpdateCategoryPayload,
} from '@/types/category';
import { ApiError } from '@/lib/api-client';
import { resolveParentParam } from './category-parent-link';
import { uploadImageViaPresign } from './product-image-upload';

// ─── Icons ──────────────────────────────────────────────────────────────────

function FolderIcon({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
    </svg>
  );
}

function TreeIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 7h4M3 12h8M3 17h4m2-5h8m0 0V7m0 5v5" />
    </svg>
  );
}

function ListIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} aria-hidden="true">
      <path strokeLinecap="round" d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
    </svg>
  );
}

function TrashIcon({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
  );
}

function PencilIcon({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125" />
    </svg>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function getUserRoleLevel(role?: string): number {
  return ROLE_LEVELS[role ?? ''] ?? 0;
}

function countTotalProducts(categories: Category[]): number {
  return categories.reduce((sum, c) => sum + c.product_count, 0);
}

// ─── KPI Stat Card (inline, glass) ──────────────────────────────────────────

function KpiMini({ label, value, tone }: { label: string; value: string | number; tone: 'brand' | 'success' | 'violet' | 'teal' }) {
  const tc = toneClasses(tone);
  return (
    <div className={cn(GLASS, 'relative overflow-hidden p-4')}>
      <div className={`absolute inset-0 bg-gradient-to-br ${tc.accent} pointer-events-none`} />
      <p className="relative text-xs font-medium text-neutral-500 dark:text-neutral-400">{label}</p>
      <p className="relative text-xl font-bold text-neutral-900 dark:text-neutral-100 tabular-nums mt-1">{value}</p>
    </div>
  );
}

// ─── Tree View ──────────────────────────────────────────────────────────────

function CategoryTreeNode({
  node,
  depth,
  canEdit,
  canDelete,
  onEdit,
  onDelete,
  onNavigate,
}: {
  node: CategoryWithChildren;
  depth: number;
  canEdit: boolean;
  canDelete: boolean;
  onEdit: (c: Category) => void;
  onDelete: (c: Category) => void;
  onNavigate: (id: string) => void;
}) {
  const [expanded, setExpanded] = useState(depth === 0);
  const hasChildren = node.children.length > 0;

  return (
    <div className="animate-fade-in">
      <div
        className={cn(
          'flex items-center gap-3 px-4 py-2.5 rounded-xl group transition-all duration-200',
          'hover:bg-white/60 dark:hover:bg-neutral-800/60',
          depth > 0 && 'ml-8 relative before:absolute before:left-0 before:top-0 before:bottom-0 before:w-px before:bg-neutral-200/60 dark:before:bg-neutral-700/40',
        )}
      >
        {/* Expand/Collapse */}
        <button
          className="flex-shrink-0 h-6 w-6 flex items-center justify-center rounded-md text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 hover:bg-neutral-100/80 dark:hover:bg-neutral-700/50 transition-all"
          onClick={() => setExpanded((v) => !v)}
          disabled={!hasChildren}
          aria-label={hasChildren ? (expanded ? 'Collapse' : 'Expand') : undefined}
          aria-expanded={hasChildren ? expanded : undefined}
        >
          {hasChildren ? (
            <svg className={cn('h-3.5 w-3.5 transition-transform duration-200', expanded && 'rotate-90')} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          ) : (
            <span className="h-1.5 w-1.5 rounded-full bg-neutral-300 dark:bg-neutral-600" />
          )}
        </button>

        {/* Image thumbnail */}
        {node.image_url ? (
          <img src={node.image_url} alt="" className="h-8 w-8 rounded-lg object-cover flex-shrink-0" />
        ) : (
          <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-indigo-100 to-violet-50 dark:from-indigo-900/30 dark:to-violet-900/20 flex items-center justify-center flex-shrink-0">
            <FolderIcon className="h-4 w-4 text-indigo-400 dark:text-indigo-500" />
          </div>
        )}

        {/* Name */}
        <button
          className="flex-1 text-left text-sm font-medium text-neutral-900 dark:text-neutral-100 hover:text-brand-600 dark:hover:text-brand-400 transition-colors truncate"
          onClick={() => onNavigate(node.id)}
        >
          {node.name}
        </button>

        {/* Meta pills */}
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-brand-50 dark:bg-brand-900/20 text-brand-600 dark:text-brand-400 tabular-nums">
          {node.product_count} {node.product_count === 1 ? 'product' : 'products'}
        </span>
        {hasChildren && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-violet-50 dark:bg-violet-900/20 text-violet-600 dark:text-violet-400 tabular-nums">
            {node.children.length} sub
          </span>
        )}

        {/* Status dot */}
        <span className={cn(
          'inline-flex items-center gap-1.5 text-xs font-medium',
          node.is_active ? 'text-emerald-600 dark:text-emerald-400' : 'text-neutral-400 dark:text-neutral-500',
        )}>
          <span className={cn(
            'h-2 w-2 rounded-full',
            node.is_active ? 'bg-emerald-500' : 'bg-neutral-300 dark:bg-neutral-600',
          )} />
          {node.is_active ? 'Active' : 'Inactive'}
        </span>

        {/* Actions — visible on hover */}
        <div className="hidden group-hover:flex items-center gap-1">
          {canEdit && (
            <Tooltip content="Edit category">
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => { e.stopPropagation(); onEdit(node); }}
                aria-label={`Edit ${node.name}`}
              >
                <PencilIcon />
              </Button>
            </Tooltip>
          )}
          {canDelete && (
            <Tooltip content="Deactivate category">
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => { e.stopPropagation(); onDelete(node); }}
                aria-label={`Deactivate ${node.name}`}
              >
                <TrashIcon className="h-4 w-4 text-danger-500" />
              </Button>
            </Tooltip>
          )}
        </div>
      </div>

      {/* Children */}
      {expanded && hasChildren && (
        <div className="animate-fade-in">
          {node.children
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((child) => (
              <CategoryTreeNode
                key={child.id}
                node={child}
                depth={depth + 1}
                canEdit={canEdit}
                canDelete={canDelete}
                onEdit={onEdit}
                onDelete={onDelete}
                onNavigate={onNavigate}
              />
            ))}
        </div>
      )}
    </div>
  );
}

// ─── Flat Table columns ─────────────────────────────────────────────────────

const flatColumns: ColumnDef<Category>[] = [
  {
    key: 'name',
    header: 'Category',
    render: (_v, row) => (
      <div className="flex items-center gap-3">
        {row.image_url ? (
          <img src={row.image_url} alt="" className="h-9 w-9 rounded-lg object-cover" />
        ) : (
          <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-indigo-100 to-violet-50 dark:from-indigo-900/30 dark:to-violet-900/20 flex items-center justify-center">
            <FolderIcon className="h-4 w-4 text-indigo-400 dark:text-indigo-500" />
          </div>
        )}
        <div className="min-w-0">
          <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100 truncate">{row.name}</p>
          {row.description && (
            <p className="text-xs text-neutral-400 dark:text-neutral-500 truncate max-w-xs">{row.description}</p>
          )}
        </div>
      </div>
    ),
  },
  {
    key: 'parent_id',
    header: 'Type',
    render: (_v, row) => (
      <Badge variant={row.parent_id ? 'info' : 'trial'} size="sm">
        {row.parent_id ? 'Subcategory' : 'Root'}
      </Badge>
    ),
  },
  {
    key: 'product_count',
    header: 'Products',
    numeric: true,
    render: (_v, row) => (
      <span className="text-sm text-neutral-700 dark:text-neutral-300 tabular-nums">{row.product_count}</span>
    ),
  },
  {
    key: 'is_active',
    header: 'Status',
    render: (_v, row) => (
      <span className={cn(
        'inline-flex items-center gap-1.5 text-xs font-medium',
        row.is_active ? 'text-emerald-600 dark:text-emerald-400' : 'text-neutral-400 dark:text-neutral-500',
      )}>
        <span className={cn(
          'h-2 w-2 rounded-full',
          row.is_active ? 'bg-emerald-500' : 'bg-neutral-300 dark:bg-neutral-600',
        )} />
        {row.is_active ? 'Active' : 'Inactive'}
      </span>
    ),
  },
];

// ─── CategoryListContent ────────────────────────────────────────────────────

type ViewMode = 'tree' | 'flat';

interface CategoryFormState {
  name: string;
  parent_id: string;
  description: string;
  is_active: boolean;
  image_url: string;
}

const DEFAULT_FORM: CategoryFormState = {
  name: '',
  parent_id: '',
  description: '',
  is_active: true,
  image_url: '',
};

function CategoryListContent() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const profile = useAuthStore((s) => s.profile);
  const userRoleLevel = getUserRoleLevel(profile?.role);

  // RBAC — MANAGER+ can edit, OWNER+ can delete (matching ProductListPage pattern)
  const canEdit = userRoleLevel >= ROLE_LEVELS.MANAGER;
  const canDelete = userRoleLevel >= ROLE_LEVELS.OWNER;

  const [viewMode, setViewMode] = useState<ViewMode>('tree');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [deletingCategory, setDeletingCategory] = useState<Category | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [form, setForm] = useState<CategoryFormState>(DEFAULT_FORM);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [imageUploading, setImageUploading] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Keyboard shortcuts (spec §3.98)
  useKeyboardShortcut('/', useCallback(() => {
    searchRef.current?.focus();
  }, []));

  useKeyboardShortcut('n', useCallback(() => {
    if (!canEdit) return;
    setEditingCategory(null);
    setForm(DEFAULT_FORM);
    setFormErrors({});
    setShowModal(true);
  }, [canEdit]));

  const { data, isLoading, error } = useQuery({
    queryKey: ['categories'],
    queryFn: () => categoryService.list(),
    staleTime: 30_000,
  });

  const allCategories = data?.data ?? [];

  // Honour ?parent=<id> deep-link from CategoryDetailPage's "Add Subcategory"
  // button: open the create modal pre-filled with that parent, then strip the
  // param so it doesn't silently re-trigger on re-render.
  const parentParam = searchParams.get('parent');
  useEffect(() => {
    const action = resolveParentParam(parentParam, allCategories, canEdit);
    if (action.kind === 'noop') return;
    if (action.kind === 'open-create-with-parent') {
      setEditingCategory(null);
      setForm({ ...DEFAULT_FORM, parent_id: action.parentId });
      setFormErrors({});
      setShowModal(true);
    }
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('parent');
      return next;
    }, { replace: true });
  }, [parentParam, canEdit, allCategories, setSearchParams]);

  // Computed stats
  const activeCount = allCategories.filter((c) => c.is_active).length;
  const rootCount = allCategories.filter((c) => !c.parent_id).length;
  const totalProducts = countTotalProducts(allCategories);

  // Filter
  const filtered = allCategories.filter((c) => {
    if (debouncedSearch && !c.name.toLowerCase().includes(debouncedSearch.toLowerCase())) return false;
    if (statusFilter === 'active' && !c.is_active) return false;
    if (statusFilter === 'inactive' && c.is_active) return false;
    return true;
  });

  const tree = buildCategoryTree(filtered);

  // Parent options for form — hierarchical with indentation
  const rootCategories = allCategories.filter((c) => !c.parent_id);
  // A subcategory preset by "Add Subcategory" (?parent=) may itself be a child;
  // list it so the picker shows the real parent instead of "None (root)".
  const presetParent = allCategories.find((c) => c.id === form.parent_id && c.parent_id);
  const parentOptions = [
    { value: '', label: '— None (root category) —' },
    ...rootCategories
      .filter((c) => !editingCategory || c.id !== editingCategory.id)
      .map((c) => ({ value: c.id, label: c.name })),
    ...(presetParent ? [{ value: presetParent.id, label: presetParent.name }] : []),
  ];

  // Mutations
  const createMutation = useMutation({
    mutationFn: (payload: CreateCategoryPayload) => categoryService.create(payload),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      toast({ title: `Category '${created.name}' created`, variant: 'success' });
      setShowModal(false);
    },
    onError: (err: Error) => {
      if (err instanceof ApiError && err.statusCode === 409) {
        setFormErrors((prev) => ({ ...prev, name: 'A category with this name already exists at this level' }));
      } else {
        toast({ title: err.message, variant: 'error' });
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateCategoryPayload }) =>
      categoryService.update(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      toast({ title: 'Category updated successfully', variant: 'success' });
      setShowModal(false);
    },
    onError: (err: Error) => {
      // CS3-005: a 409 is either the name clash or active products blocking the Active toggle.
      if (err instanceof ApiError && err.code === 'CATEGORY_HAS_ACTIVE_PRODUCTS') {
        toast({ title: 'Cannot deactivate category', description: err.message, variant: 'error' });
      } else if (err instanceof ApiError && err.statusCode === 409) {
        setFormErrors((prev) => ({ ...prev, name: 'A category with this name already exists at this level' }));
      } else {
        toast({ title: err.message, variant: 'error' });
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => categoryService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      toast({ title: 'Category deactivated', variant: 'success' });
      setDeletingCategory(null);
      setDeleteError(null);
    },
    onError: (err: Error) => {
      if (err instanceof ApiError && err.code === 'CATEGORY_HAS_ACTIVE_PRODUCTS') {
        setDeleteError(err.message);
      } else {
        toast({ title: err.message, variant: 'error' });
      }
    },
  });

  function openCreate(parentId?: string) {
    if (!canEdit) return;
    setEditingCategory(null);
    setForm({ ...DEFAULT_FORM, parent_id: parentId ?? '' });
    setFormErrors({});
    setShowModal(true);
  }

  function openEdit(c: Category) {
    if (!canEdit) return;
    setEditingCategory(c);
    setForm({
      name: c.name,
      parent_id: c.parent_id ?? '',
      description: c.description ?? '',
      is_active: c.is_active,
      image_url: c.image_url ?? '',
    });
    setFormErrors({});
    setShowModal(true);
  }

  function validate(): boolean {
    const errs: Record<string, string> = {};
    const nameErr = validateCategoryName(form.name);
    if (nameErr) errs.name = nameErr;
    if (form.description && stripHtml(form.description).length > CATEGORY_DESC_MAX) {
      errs.description = `Description must be at most ${CATEGORY_DESC_MAX} characters`;
    }
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  }

  const handleSubmit = useCallback(() => {
    if (!validate()) return;
    const payload: CreateCategoryPayload = {
      name: stripHtml(form.name).trim(),
      parent_id: form.parent_id || null,
      description: stripHtml(form.description).trim() || null,
      image_url: form.image_url || null,
    };
    if (editingCategory) {
      updateMutation.mutate({ id: editingCategory.id, payload: { ...payload, is_active: form.is_active } });
    } else {
      // A new category is always active: POST /categories takes no is_active
      // (FORMS.md §5.7), and the strict whitelist 400s if one is sent.
      createMutation.mutate(payload);
    }
  }, [form, editingCategory]);

  async function handleImageUpload(files: File[]) {
    if (!files.length) {
      setForm((f) => ({ ...f, image_url: '' }));
      return;
    }
    const file = files[0];
    setImageUploading(true);
    try {
      const key = await uploadImageViaPresign(file, 'category_image');
      setForm((f) => ({ ...f, image_url: key }));
    } catch {
      toast({ title: 'Image upload failed', variant: 'error' });
    } finally {
      setImageUploading(false);
    }
  }

  const isSaving = createMutation.isPending || updateMutation.isPending;

  if (error) {
    return (
      <Alert
        variant="danger"
        title="Failed to load categories"
        description="An error occurred loading the category list. Please try refreshing."
        action={{ label: 'Retry', onClick: () => queryClient.invalidateQueries({ queryKey: ['categories'] }) }}
      />
    );
  }

  const hasNoData = !isLoading && allCategories.length === 0;
  const hasFilteredResults = filtered.length > 0;

  return (
    <div className="animate-fade-in">
      {/* ─── Action Buttons ──────────────────────────────────────── */}
      {canEdit && (
        <div className="flex items-center gap-2 mb-6">
          <Button variant="primary" onClick={() => openCreate()}>
            <svg className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
            Add Category
          </Button>
          <Button variant="secondary" onClick={() => openCreate()}>
            Add Subcategory
          </Button>
        </div>
      )}

      {/* ─── KPI Stats ─────────────────────────────────────────────── */}
      {!isLoading && allCategories.length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6 animate-slide-up">
          <KpiMini label="Total Categories" value={allCategories.length} tone="brand" />
          <KpiMini label="Active" value={activeCount} tone="success" />
          <KpiMini label="Root Categories" value={rootCount} tone="violet" />
          <KpiMini label="Products Assigned" value={totalProducts.toLocaleString('en-IN')} tone="teal" />
        </div>
      )}

      {/* ─── Filter Bar (Glass) ────────────────────────────────────── */}
      <div className={cn(GLASS_FILTER, 'p-3 mb-6')}>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          {/* Search */}
          <div className="w-full sm:w-72">
            <Input
              ref={searchRef}
              variant="search"
              value={search}
              onChange={(v) => setSearch(typeof v === 'string' ? v : String(v))}
              placeholder="Search categories... (/)"
              aria-label="Search categories"
            />
          </div>

          {/* Status filter chips */}
          <div className="flex items-center gap-2">
            {(['all', 'active', 'inactive'] as const).map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={cn(
                  'px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 capitalize',
                  statusFilter === s
                    ? 'bg-brand-500/15 text-brand-700 dark:text-brand-300 shadow-sm'
                    : 'bg-white/50 dark:bg-neutral-800/50 text-neutral-500 dark:text-neutral-400 hover:bg-white/80 dark:hover:bg-neutral-700/60',
                )}
              >
                {s}
              </button>
            ))}
          </div>

          {/* View toggle */}
          <div className="flex rounded-xl overflow-hidden ml-auto" role="radiogroup" aria-label="View mode">
            <button
              type="button"
              role="radio"
              aria-checked={viewMode === 'tree'}
              aria-label="Tree view"
              onClick={() => setViewMode('tree')}
              className={cn(
                'flex items-center justify-center gap-1.5 h-8 px-3 text-xs font-medium transition-all duration-150',
                viewMode === 'tree'
                  ? 'bg-brand-600 text-white'
                  : 'bg-white/80 dark:bg-neutral-800/80 text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300',
              )}
            >
              <TreeIcon />
              Tree
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={viewMode === 'flat'}
              aria-label="List view"
              onClick={() => setViewMode('flat')}
              className={cn(
                'flex items-center justify-center gap-1.5 h-8 px-3 text-xs font-medium transition-all duration-150',
                viewMode === 'flat'
                  ? 'bg-brand-600 text-white'
                  : 'bg-white/80 dark:bg-neutral-800/80 text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300',
              )}
            >
              <ListIcon />
              List
            </button>
          </div>
        </div>
      </div>

      {/* ─── Content ───────────────────────────────────────────────── */}
      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} variant="rect" height={48} />
          ))}
        </div>
      ) : hasNoData ? (
        <div className="animate-slide-up">
          <EmptyState
            variant="page"
            icon={
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-500/20 to-violet-500/20">
                <FolderIcon className="h-8 w-8 text-indigo-500" />
              </div>
            }
            title="No categories yet"
            description="Organize your products with categories and subcategories"
            action={canEdit ? { label: 'Add Category', onClick: () => openCreate() } : undefined}
          />
        </div>
      ) : viewMode === 'tree' ? (
        !hasFilteredResults ? (
          <EmptyState
            variant="inline"
            title="No categories match"
            description="Try a different search term or filter"
          />
        ) : (
          <div className={cn(GLASS, 'p-3 animate-slide-up')}>
            {tree.map((node) => (
              <CategoryTreeNode
                key={node.id}
                node={node}
                depth={0}
                canEdit={canEdit}
                canDelete={canDelete}
                onEdit={openEdit}
                onDelete={(c) => { setDeletingCategory(c); setDeleteError(null); }}
                onNavigate={(id) => navigate(`/categories/${id}`)}
              />
            ))}
          </div>
        )
      ) : (
        <div className="animate-slide-up">
          <DataTable<Category>
            columns={[
              ...flatColumns,
              {
                key: 'id' as keyof Category,
                header: '',
                render: (_v: unknown, row: Category) => (
                  <div className="flex gap-1 justify-end">
                    <Button variant="ghost" size="sm" onClick={() => navigate(`/categories/${row.id}`)}>
                      View
                    </Button>
                    {canEdit && (
                      <Tooltip content="Edit">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(row)} aria-label={`Edit ${row.name}`}>
                          <PencilIcon />
                        </Button>
                      </Tooltip>
                    )}
                    {canDelete && (
                      <Tooltip content="Deactivate">
                        <Button variant="ghost" size="sm" onClick={() => { setDeletingCategory(row); setDeleteError(null); }} aria-label={`Deactivate ${row.name}`}>
                          <TrashIcon className="h-4 w-4 text-danger-500" />
                        </Button>
                      </Tooltip>
                    )}
                  </div>
                ),
              },
            ]}
            data={filtered}
            emptyState={{
              title: 'No categories match',
              description: debouncedSearch ? 'Try a different search term' : 'Try adjusting your filters',
            }}
          />
        </div>
      )}

      {/* ─── Mobile FAB ────────────────────────────────────────────── */}
      {canEdit && (
        <div className="fixed bottom-20 right-4 sm:hidden z-30">
          <Button
            variant="primary"
            onClick={() => openCreate()}
            className="h-14 w-14 rounded-full shadow-lg"
            aria-label="Add category"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
          </Button>
        </div>
      )}

      {/* ─── Create / Edit Modal ───────────────────────────────────── */}
      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        title={editingCategory ? `Edit "${editingCategory.name}"` : 'New Category'}
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setShowModal(false)} disabled={isSaving}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSubmit} loading={isSaving}>
              {editingCategory ? 'Save Changes' : 'Create Category'}
            </Button>
          </div>
        }
      >
        <form
          onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}
          className="space-y-4"
        >
          <FormField label="Category Name" required error={formErrors.name}>
            <Input
              value={form.name}
              onChange={(v) => {
                setForm((f) => ({ ...f, name: typeof v === 'string' ? v : String(v) }));
                if (formErrors.name) setFormErrors((prev) => { const next = { ...prev }; delete next.name; return next; });
              }}
              placeholder="e.g. Beverages"
            />
          </FormField>

          <FormField label="Parent Category">
            <Select
              options={parentOptions}
              value={form.parent_id}
              onChange={(v) => setForm((f) => ({ ...f, parent_id: typeof v === 'string' ? v : '' }))}
              placeholder="None (root)"
            />
          </FormField>

          <FormField label="Description" error={formErrors.description}>
            <TextArea
              value={form.description}
              onChange={(v) => setForm((f) => ({ ...f, description: v }))}
              placeholder="Optional description..."
              rows={2}
              maxLength={CATEGORY_DESC_MAX}
              resize="none"
            />
          </FormField>

          <FormField label="Category Image">
            <FileUpload
              variant="button"
              accept={CATEGORY_IMAGE_ACCEPT}
              maxSize={CATEGORY_IMAGE_MAX_SIZE}
              preview={form.image_url || null}
              onUpload={handleImageUpload}
              loading={imageUploading}
            />
          </FormField>

          {editingCategory && (
            <div className="grid grid-cols-2 gap-4">
              <FormField label="Status">
                <div className="flex items-center h-10">
                  <Toggle
                    checked={form.is_active}
                    onChange={(v) => setForm((f) => ({ ...f, is_active: v }))}
                    label={form.is_active ? 'Active' : 'Inactive'}
                  />
                </div>
              </FormField>
            </div>
          )}

          {/* Hidden submit for Enter key */}
          <button type="submit" className="sr-only" tabIndex={-1}>Submit</button>
        </form>
      </Modal>

      {/* ─── Deactivate Confirm ────────────────────────────────────── */}
      <ConfirmDialog
        open={!!deletingCategory}
        onCancel={() => { setDeletingCategory(null); setDeleteError(null); }}
        onConfirm={() => deletingCategory && deleteMutation.mutate(deletingCategory.id)}
        title={`Deactivate "${deletingCategory?.name}"?`}
        description={
          deletingCategory?.product_count
            ? `Move or deactivate its ${deletingCategory.product_count} product${deletingCategory.product_count !== 1 ? 's' : ''} first.`
            : 'It stays in the list as Inactive, and you can turn it back on.'
        }
        confirmLabel="Deactivate"
        confirmDisabled={(deletingCategory?.product_count ?? 0) > 0}
        variant="danger"
        loading={deleteMutation.isPending}
      />

      {/* Deactivate error alert */}
      {deleteError && (
        <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-96 z-50">
          <Alert
            variant="danger"
            title="Cannot deactivate category"
            description={deleteError}
            dismissible
            onDismiss={() => setDeleteError(null)}
          />
        </div>
      )}
    </div>
  );
}

// ─── Export with ErrorBoundary ───────────────────────────────────────────────

export function CategoryListPage() {
  return (
    <PageContainer
      title="Categories"
      subtitle="Organise your products into categories and subcategories"
      breadcrumbs={[{ label: 'Categories' }]}
    >
      <ErrorBoundary>
        <CategoryListContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
